import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { X as XIcon, Loader2, Mail, Plus, Trash2, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Modal reutilisable pour envoyer n'importe quel document PDF genere par CHANTIX
 * a des destinataires. Deux modes :
 *  - Mode 1 (doc_type/doc_id connu backend) : appelle POST /api/documents/send-email
 *  - Mode 2 (URL pdf directe) : telecharge le PDF via `pdfUrl` puis l'envoie via
 *    POST /api/documents/send-file-email (upload multipart). Utile pour Journal,
 *    CSPS docs, Planning dont la generation PDF vit inline dans server.py.
 *
 * Props:
 *  - open (bool)
 *  - onClose ()
 *  - docType: string
 *  - docId: string (mode 1 uniquement)
 *  - pdfUrl: string (mode 2, URL du PDF a telecharger puis renvoyer)
 *  - filename: string (mode 2, nom du fichier joint)
 *  - subject: string (mode 2, sujet email)
 *  - docTitle: string (affichage)
 *  - suggestedRecipients: [{email, name?, label?}]
 */
export function SendDocumentEmailDialog({ open, onClose, docType, docId, pdfUrl, filename, subject, docTitle, suggestedRecipients = [] }) {
  const [selected, setSelected] = useState({});
  const [customEmails, setCustomEmails] = useState([]);
  const [customInput, setCustomInput] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const reset = useCallback(() => {
    // Pre-cocher tous les destinataires suggeres ayant un email
    const init = {};
    suggestedRecipients.forEach(r => { if (r.email) init[r.email] = true; });
    setSelected(init);
    setCustomEmails([]);
    setCustomInput('');
    setMessage('');
  }, [suggestedRecipients]);

  useEffect(() => { if (open) reset(); }, [open, reset]);

  const addCustom = () => {
    const v = (customInput || '').trim();
    if (!v) return;
    if (!/.+@.+\..+/.test(v)) { toast.error('Email invalide'); return; }
    if (customEmails.includes(v)) { toast.error('Déjà ajouté'); return; }
    setCustomEmails(prev => [...prev, v]);
    setCustomInput('');
  };

  const removeCustom = (e) => setCustomEmails(prev => prev.filter(x => x !== e));

  const send = async () => {
    const suggestedEmails = suggestedRecipients.filter(r => r.email && selected[r.email]).map(r => r.email);
    const all = [...new Set([...suggestedEmails, ...customEmails])];
    if (all.length === 0) { toast.error('Sélectionnez au moins 1 destinataire'); return; }
    setSending(true);
    try {
      let r;
      if (pdfUrl) {
        // Mode 2 : telecharge PDF puis upload en pj
        const pdfResp = await axios.get(pdfUrl, { withCredentials: true, responseType: 'blob' });
        const fd = new FormData();
        fd.append('file', pdfResp.data, filename || 'document.pdf');
        fd.append('recipients', all.join(','));
        fd.append('subject', subject || 'Document CHANTIX');
        fd.append('message', message.trim());
        fd.append('doc_type', docType || 'generic');
        if (docId) fd.append('doc_id', docId);
        r = await axios.post(`${API}/documents/send-file-email`, fd, {
          withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        // Mode 1 : backend genere le PDF via doc_type/doc_id
        r = await axios.post(`${API}/documents/send-email`, {
          doc_type: docType,
          doc_id: docId,
          recipients: all,
          message: message.trim(),
        }, { withCredentials: true });
      }
      const nb = r.data.sent || 0;
      const total = r.data.total || all.length;
      if (nb === total) {
        toast.success(`Envoyé à ${nb} destinataire(s)`);
      } else {
        toast.warning(`${nb}/${total} envoyés — ${r.data.failed?.length || 0} échec(s)`);
      }
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur envoi');
    } finally { setSending(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-[10000] flex items-center justify-center p-4" data-testid="send-doc-email-dialog">
      <div className="bg-white rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-bold text-[#111827] flex items-center gap-2">
            <Mail size={22} className="text-[#0055FF]" />
            Envoyer par email
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100" data-testid="send-doc-close">
            <XIcon size={20} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {docTitle && (
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm text-blue-900">
              📎 <strong>Pièce jointe :</strong> {docTitle}
            </div>
          )}

          {/* Destinataires suggerees */}
          {suggestedRecipients.length > 0 && (
            <div>
              <Label className="mb-2 block">Participants du chantier</Label>
              <div className="space-y-1 max-h-48 overflow-y-auto border rounded-lg p-2 bg-gray-50">
                {suggestedRecipients.map((r, i) => {
                  const disabled = !r.email;
                  return (
                    <label key={i}
                      className={`flex items-center gap-2 p-2 rounded ${disabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-white cursor-pointer'}`}
                      data-testid={`recipient-toggle-${i}`}>
                      <input type="checkbox" checked={!!selected[r.email]} disabled={disabled}
                        onChange={() => setSelected(s => ({ ...s, [r.email]: !s[r.email] }))}
                        className="h-4 w-4 accent-[#0055FF]" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#111827]">{r.label || r.name || r.email || '(sans nom)'}</p>
                        {r.email ? (
                          <p className="text-xs text-gray-500">{r.email}</p>
                        ) : (
                          <p className="text-xs text-red-500 italic">Email manquant — impossible d&apos;envoyer</p>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* Emails libres */}
          <div>
            <Label className="mb-2 block">Autres destinataires</Label>
            <div className="flex gap-2">
              <Input value={customInput} onChange={e => setCustomInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
                placeholder="email@example.com" data-testid="custom-email-input" />
              <Button onClick={addCustom} variant="outline" data-testid="add-custom-btn"><Plus size={14} /></Button>
            </div>
            {customEmails.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {customEmails.map(e => (
                  <span key={e} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-full">
                    {e}
                    <button onClick={() => removeCustom(e)} className="hover:text-red-600" data-testid={`remove-custom-${e}`}>
                      <Trash2 size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Message personnalise */}
          <div>
            <Label htmlFor="msg-doc-email">Message personnalisé (optionnel)</Label>
            <textarea id="msg-doc-email" rows={3} value={message} onChange={e => setMessage(e.target.value)}
              className="w-full border rounded-md p-2 text-sm mt-1" data-testid="custom-message"
              placeholder="Ex: Bonjour, veuillez trouver le compte rendu. Toute remarque à me remonter sous 5 jours." />
          </div>

          <div className="text-xs text-gray-500 bg-gray-50 border rounded p-2">
            📧 Vous êtes en Reply-To : les destinataires vous répondront directement.
          </div>

          <div className="flex gap-2 pt-1">
            <Button variant="outline" onClick={onClose} className="flex-1" data-testid="send-doc-cancel">Annuler</Button>
            <Button onClick={send} disabled={sending} className="flex-1 bg-[#0055FF] hover:bg-blue-700" data-testid="send-doc-confirm">
              {sending ? <Loader2 className="animate-spin h-4 w-4" /> : <><Check size={16} className="mr-1" />Envoyer</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
